import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { useSession, offlineAuthorizationFor } from '../state/session.tsx';
import { staffManagementCopy } from '../../shared/staff-management.ts';
import { LoadingBlock } from '../components/ui.tsx';
import { WorkPage, WorkSection, WorkEmpty, StaffNavigation } from '../components/work-page.tsx';

type Person = { id: string; name: string; muted: number };
type Options = { people: Person[]; teams: { id: string; name: string }[]; canReadTeams: boolean; truncated: boolean };
type Pending = { path: string; data: unknown; announcement: boolean };

export default function StaffManagementPage() {
  const s = useSession(),
    { i18n } = useTranslation();
  const copy = staffManagementCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  if (
    !s.user ||
    !s.company ||
    s.company.accessLocked ||
    !s.can('staff_portal:manage') ||
    !s.company.planFeatures?.includes('staff_portal')
  )
    return (
      <WorkPage title={copy.title}>
        <WorkSection title={i18n.language.startsWith('es') ? 'Acceso requerido' : 'Access required'}>
          <p role="status">{copy.blocked}</p>
        </WorkSection>
      </WorkPage>
    );
  return (
    <ManagementContent
      key={JSON.stringify([
        s.user.companyId,
        s.user.id,
        s.currentLocation?.id,
        offlineAuthorizationFor(s.user, s.company),
      ])}
    />
  );
}
function ManagementContent() {
  const { i18n } = useTranslation(),
    copy = staffManagementCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  const [options, setOptions] = useState<Options | null>(null),
    [kind, setKind] = useState<'everyone' | 'people' | 'teams'>('everyone');
  const [ids, setIds] = useState<string[]>([]),
    [title, setTitle] = useState(''),
    [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false),
    [ack, setAck] = useState(false),
    [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(''),
    [uncertain, setUncertain] = useState(false),
    [denied, setDenied] = useState(false);
  const alive = useRef(true),
    generation = useRef(0),
    pending = useRef<Pending | null>(null),
    lock = useRef(false);
  useEffect(() => {
    alive.current = true;
    void refresh();
    return () => {
      alive.current = false;
      generation.current++;
      pending.current = null;
    };
  }, []);
  async function refresh() {
    const gen = ++generation.current;
    try {
      const data = await api.get<Options>('/staff/announcement-audiences');
      if (alive.current && generation.current === gen) {
        setOptions(data);
        if (denied) setMessage('');
        setDenied(false);
      }
    } catch (error) {
      if (alive.current && generation.current === gen) {
        const status = (error as { status?: number }).status;
        if (status && [401, 402, 403, 404, 410].includes(status)) {
          setOptions(null);
          setTitle('');
          setBody('');
          setIds([]);
          pending.current = null;
          setUncertain(false);
          setDenied(true);
        }
        setMessage(copy.error);
      }
    }
  }
  async function submit(request: Pending) {
    if (lock.current) return;
    if (!navigator.onLine) {
      setMessage(copy.offline);
      return;
    }
    lock.current = true;
    setBusy(true);
    setMessage('');
    const gen = generation.current;
    pending.current ??= request;
    try {
      await api.post(pending.current.path, pending.current.data);
      if (!alive.current || gen !== generation.current) return;
      const wasAnnouncement = pending.current.announcement;
      pending.current = null;
      setUncertain(false);
      if (wasAnnouncement) {
        setTitle('');
        setBody('');
        setIds([]);
        setKind('everyone');
        setPinned(false);
        setAck(false);
        setMessage(copy.saved);
      }
      await refresh();
    } catch (error) {
      if (!alive.current || gen !== generation.current) return;
      const status = (error as { status?: number }).status;
      if (status && status >= 400 && status < 500 && status !== 408) {
        pending.current = null;
        setUncertain(false);
        setMessage(copy.error);
      } else {
        setUncertain(true);
        setMessage(copy.uncertain);
      }
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const frozen = busy || uncertain;
  const choices = kind === 'teams' ? options?.teams : options?.people;
  const unavailable = kind !== 'everyone' && ids.some((id) => !choices?.some((row) => row.id === id));
  if (denied)
    return (
      <WorkPage title={copy.title}>
        <WorkSection title={i18n.language.startsWith('es') ? 'Acceso requerido' : 'Access required'}>
          <p role="status">{copy.blocked}</p>
          <button type="button" className="btn btn-secondary" onClick={() => void refresh()}>
            {copy.refresh}
          </button>
        </WorkSection>
      </WorkPage>
    );
  return (
    <WorkPage
      title={copy.title}
      description={
        i18n.language.startsWith('es')
          ? 'Envíe anuncios y administre el acceso para publicar en su ubicación.'
          : 'Send announcements and manage posting access for your location.'
      }
      actions={
        <button type="button" className="btn btn-secondary" disabled={frozen} onClick={() => void refresh()}>
          {copy.refresh}
        </button>
      }
    >
      <StaffNavigation active="manage" canManage clientNavigation />
      {message && (
        <p role="status" className="rounded border p-3">
          {message}
        </p>
      )}
      {!options ? (
        !message && <LoadingBlock label={copy.loading} />
      ) : (
        <>
          <form
            className="card space-y-4 p-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (kind !== 'everyone' && !ids.length) {
                setMessage(copy.selected);
                return;
              }
              if (unavailable) {
                setMessage(copy.unavailable);
                return;
              }
              void submit({
                path: '/staff/announcements',
                announcement: true,
                data: {
                  clientMutationId: crypto.randomUUID(),
                  title,
                  body,
                  audienceType: kind,
                  recipientIds: kind === 'people' ? ids : [],
                  teamIds: kind === 'teams' ? ids : [],
                  pinned,
                  requiresAck: ack,
                },
              });
            }}
          >
            <h2 className="text-lg font-semibold">
              {i18n.language.startsWith('es') ? 'Nuevo anuncio' : 'New announcement'}
            </h2>
            <div>
              <label className="block" htmlFor="staff-announcement-title">
                {copy.titleLabel}
              </label>
              <input
                id="staff-announcement-title"
                className="field-control mt-1 w-full"
                value={title}
                maxLength={200}
                required
                disabled={frozen}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="block" htmlFor="staff-announcement-body">
                {copy.body}
              </label>
              <textarea
                id="staff-announcement-body"
                className="field-control mt-1 w-full"
                rows={5}
                value={body}
                maxLength={4000}
                required
                disabled={frozen}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
            <div>
              <label className="block" htmlFor="staff-announcement-audience">
                {copy.audience}
              </label>
              <select
                id="staff-announcement-audience"
                className="field-control mt-1 w-full"
                value={kind}
                disabled={frozen}
                onChange={(e) => {
                  setKind(e.target.value as typeof kind);
                  setIds([]);
                }}
              >
                <option value="everyone">{copy.everyone}</option>
                <option value="people">{copy.people}</option>
                {options.canReadTeams && <option value="teams">{copy.teams}</option>}
              </select>
            </div>
            {kind !== 'everyone' && (
              <fieldset className="max-h-64 overflow-y-auto rounded border p-2" disabled={frozen}>
                <legend>{kind === 'teams' ? copy.teams : copy.people}</legend>
                {!choices?.length && <p>{copy.empty}</p>}
                {choices?.map((row) => (
                  <label key={row.id} className="flex min-h-11 items-center gap-3 break-words py-1">
                    <input
                      type="checkbox"
                      checked={ids.includes(row.id)}
                      onChange={(e) =>
                        setIds((old) => (e.target.checked ? [...old, row.id] : old.filter((id) => id !== row.id)))
                      }
                    />
                    <span className="min-w-0 flex-1 break-words">{row.name}</span>
                  </label>
                ))}
              </fieldset>
            )}
            {unavailable && (
              <div className="space-y-2">
                <p role="status">{copy.unavailable}</p>
                <button type="button" className="btn btn-secondary" disabled={frozen} onClick={() => setIds([])}>
                  {copy.clearSelection}
                </button>
              </div>
            )}
            <p>{copy.visibility}</p>
            <p className="text-sm">{copy.snapshot}</p>
            {options.truncated && <p>{copy.truncated}</p>}
            <details>
              <summary className="min-h-11 cursor-pointer content-center">{copy.options}</summary>
              <label className="flex min-h-11 items-center gap-2">
                <input
                  type="checkbox"
                  checked={pinned}
                  disabled={frozen}
                  onChange={(e) => setPinned(e.target.checked)}
                />
                {copy.pinned}
              </label>
              <label className="flex min-h-11 items-center gap-2">
                <input type="checkbox" checked={ack} disabled={frozen} onChange={(e) => setAck(e.target.checked)} />
                {copy.ack}
              </label>
            </details>
            <button
              className="btn btn-primary min-h-11 w-full sm:w-auto"
              disabled={frozen || unavailable || !title.trim() || !body.trim() || (kind !== 'everyone' && !ids.length)}
            >
              {copy.publish}
            </button>
          </form>
          <button
            type="button"
            className="block min-h-11 text-left font-semibold text-[color:var(--color-brand)] underline disabled:opacity-50"
            disabled={frozen}
            onClick={() => {
              if (lock.current || pending.current) return;
              const hasDraft = title || body || kind !== 'everyone' || ids.length || pinned || ack;
              if (hasDraft && !window.confirm(copy.discardForPhotos)) return;
              setTitle('');
              setBody('');
              setKind('everyone');
              setIds([]);
              setPinned(false);
              setAck(false);
              window.location.assign('/staff/photos');
            }}
          >
            {copy.photoUpdate}
          </button>
          <details className="card space-y-3 p-4">
            <summary className="min-h-11 cursor-pointer content-center font-semibold">{copy.moderation}</summary>
            <p>{copy.pauseHint}</p>
            {!options.people.length && (
              <WorkEmpty
                title={
                  i18n.language.startsWith('es')
                    ? 'No hay cuentas para publicar disponibles'
                    : 'No posting accounts available'
                }
              />
            )}
            {options.people.map((person) => (
              <div key={person.id} className="flex flex-wrap items-center justify-between gap-3 border-b py-2">
                <div className="min-w-0 break-words">
                  {person.name}
                  <p className="text-sm">{person.muted ? copy.muted : copy.active}</p>
                </div>
                <button
                  className="btn btn-secondary"
                  disabled={frozen}
                  onClick={() =>
                    void submit({
                      path: `/staff/people/${encodeURIComponent(person.id)}/mute`,
                      data: { muted: !person.muted },
                      announcement: false,
                    })
                  }
                >
                  {person.muted ? copy.unmute : copy.mute}
                </button>
              </div>
            ))}
          </details>
        </>
      )}
      {uncertain && (
        <button
          className="btn btn-primary"
          disabled={busy}
          onClick={() => {
            if (pending.current) void submit(pending.current);
          }}
        >
          {pending.current?.announcement ? copy.retry : copy.retryChange}
        </button>
      )}
    </WorkPage>
  );
}
